import React, { lazy, Suspense, useState, useCallback, useEffect, useRef, FC, startTransition } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate, useParams } from 'react-router-dom';
import { initGA, trackPageView } from './services/analytics';

initGA();
import axios from 'axios';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { userSettingsQuery } from './queries/userSettings';
import { API_ENDPOINT } from './apiEndpoints';
import styles from './App.module.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LikesProvider } from './contexts/LikesContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ToastNotifications from './components/ToastNotifications';
import GlobalPhotoUploadStatus from './components/GlobalPhotoUploadStatus';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PromoCodeGate from './components/auth/PromoCodeGate';

// Legacy redirect component - redirects from /AlbumPage/:id to /album/:id for backward compatibility
const LegacyAlbumRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/album/${id}`} replace />;
};

/** Old bookmarks/links used /ArtistPage/:id — canonical public profile is /artist/:slug|public_id|id */
const LegacyArtistPageRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/artist/${id}`} replace />;
};

const GlobalKeyListener = lazy(() => import('./components/GlobalKeyListener'));
//fixed components
const Sidebar = lazy(() => import('./components/Sidebar'));
const InfoSidebar = lazy(() => import('./components/InfoSidebar'));
const Navbar = lazy(() => import('./components/Navbar'));
const LandingPage = lazy(() => import('./components/LandingPage'));
const GenreTagPage = lazy(() => import('./components/GenreTagPage'));


const EditProfile = lazy(() => import('./components/profile/EditProfile'));
const Admin = lazy(() => import('./components/Admin'));
const ActiveMountpoints = lazy(() => import('./components/Broadcast/ActiveMountpoints'));
const AlbumCreate = lazy(() => import('./components/Artist/AlbumCreate'));
const AlbumEdit = lazy(() => import('./components/Artist/AlbumEdit'));
const AlbumList = lazy(() => import('./components/Artist/AlbumList'));
const AlbumPage = lazy(() => import('./components/Artist/AlbumPage'));
const ArtistPage = lazy(() => import('./components/Artist/ArtistPage'));

/** Remount artist profile when :id changes so in-page search navigation always gets a fresh tree (slug ↔ slug, etc.). */
function ArtistPageRoute(props: {
  selectedTrack: any;
  setSelectedTrack: any;
  currentTrackIndex: any;
  setCurrentTrackIndex: any;
  setSelectedStation: any;
  infoSidebarWidth?: number;
}) {
  const { id } = useParams();
  return <ArtistPage key={id} {...props} />;
}
const ArtistHomepage = lazy(() => import('./components/Artist/ArtistHomepage'));
const ArtistDashboard = lazy(() => import('./components/Artist/ArtistDashboard'));
const ArtistList = lazy(() => import('./components/profile/ArtistList'));
const Broadcast = lazy(() => import('./components/Broadcast/Broadcast'));
const BroadcastDashboardTabs = lazy(() => import('./components/Broadcast/BroadcastDashboardTabs'));
const IcecastBroadcaster = lazy(() => import('./components/Broadcast/IcecastBroadcaster'));
const Chatrooms = lazy(() => import('./components/Chatrooms'));
const CreateArtistProfile = lazy(() => import('./components/profile/CreateArtistProfile'));
const CreateSupporterProfile = lazy(() => import('./components/profile/CreateSupporterProfile'));
const ArtistEdit = lazy(() => import('./components/profile/ArtistEdit'));
const UploadTracks = lazy(() => import('./components/Artist/UploadTracks'));
const UploadSheetMusic = lazy(() => import('./components/Artist/UploadSheetMusic'));
const UploadStem = lazy(() => import('./components/Artist/UploadStem'));
const UploadPacks = lazy(() => import('./components/Artist/UploadPacks'));
const Ether = lazy(() => import('./components/Broadcast/Ether'));
const EtherGenre = lazy(() => import('./components/Broadcast/EtherGenre'));
const EtherMood = lazy(() => import('./components/Broadcast/EtherMood'));
const EtherInstruments = lazy(() => import('./components/Broadcast/EtherInstruments'));
const EtherTags = lazy(() => import('./components/Broadcast/EtherTags'));
const EtherLocation = lazy(() => import('./components/Broadcast/EtherLocation'));
const Home = lazy(() => import('./components/Home'));
const Charts = lazy(() => import('./components/Charts'));
const Catalog = lazy(() => import('./components/Catalog'));
const LibraryGrid = lazy(() => import('./components/LibraryGrid'));
const Likes = lazy(() => import('./components/Likes'));
const ListeningHistoryDisplay = lazy(() => import('./components/ListeningHistoryDisplay'));
const NotificationsPage = lazy(() => import('./components/NotificationsPage'));
const MessagesPage = lazy(() => import('./components/MessagesPage'));
const AdminMessageThread = lazy(() => import('./components/AdminMessageThread'));
const Payments = lazy(() => import('./components/Payments'));
const LinerNotes = lazy(() => import('./components/Artist/LinerNotes'));
const Playbar = lazy(() => import('./components/Playbar'));
const ListeningHistory = lazy(() => import('./components/ListeningHistory'));
const Profile = lazy(() => import('./components/Profile'));
const SupporterList = lazy(() => import('./components/profile/SupporterList'));
const OfflineMusic = lazy(() => import('./components/OfflineMusic'));
const OfflineMusicArtist = lazy(() => import('./components/OfflineMusicArtist'));
const OfflineMusicCollection = lazy(() => import('./components/OfflineMusicCollection'));
const Playlist = lazy(() => import('./components/Playlist'));
const PlaylistList = lazy(() => import('./components/PlaylistList'));
const PlaylistManage = lazy(() => import('./components/PlaylistManage'));
const ProfileIcon = lazy(() => import('./components/profile/ProfileIcon'));
const RadioStations = lazy(() => import('./components/Broadcast/RadioStations'));
const BuskRadio = lazy(() => import('./components/BuskRadio'));
const BuskRadioCreate = lazy(() => import('./components/BuskRadioCreate'));
const BuskRadioEdit = lazy(() => import('./components/BuskRadioEdit'));
const MixVibesShowcase = lazy(() => import('./components/MixVibesShowcase'));
const Sandbox = lazy(() => import('./components/Sandbox'));
const Search = lazy(() => import('./components/modules/Search'));
const Songs = lazy(() => import('./components/myMusic/MyMusic.component'));
const SupporterDashboard = lazy(() => import('./components/Supporter/SupporterDashboard'));
const TrackEdit = lazy(() => import('./components/Artist/TrackEdit'));
const TrackList = lazy(() => import('./components/TrackList'));
const TrackForm = lazy(() => import('./components/Artist/TrackForm'));
const EmbeddedTrackPlayer = lazy(() => import('./components/embed/EmbeddedTrackPlayer'));
const EmbeddedAlbumPlayer = lazy(() => import('./components/embed/EmbeddedAlbumPlayer'));

const UploadForm = lazy(() => import('./components/upload/UploadForm'));
const Concerts = lazy(() => import('./components/Concerts'));
const Location = lazy(() => import('./components/Location'));
const CalendarPage = lazy(() => import('./components/CalendarPage'));
const VideoUploader = lazy(() => import('./components/upload/VideoUploader'));
const ZphotoList = lazy(() => import('./components/ZphotoList'));

// Auth components
const Login = lazy(() => import('./components/auth/Login'));
const Register = lazy(() => import('./components/auth/Register'));
const VerifyEmail = lazy(() => import('./components/auth/VerifyEmail'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/auth/ResetPassword'));


// Types and interfaces
interface User {
  id: number;
  email: string;
  role: string;
  artistname?: string;
  supportername?: string;
  fileurl?: string;
  bio?: string;
  current_account_type: 'artist' | 'supporter' | 'user';
  current_artist_id?: number | null;
}

interface Track {
  id: number;
  public_id?: string | null;
  track_public_id?: string | null;
  title: string;
  artist: string;
  location: string;
  artist_id?: number;
  artist_public_id?: string | null;
  artist_slug?: string | null;
  album_id?: number;
  album_public_id?: string | null;
  album_image?: string;
  albumname?: string;
  artist_image?: string;
  artist_name?: string;
  time?: string;
  tags?: string | string[];
  genres?: string | string[];
  artistid?: number;
  albumid?: number;
}

interface Station {
  id: number;
  name: string;
  url: string;
  description?: string;
  image?: string;
}

interface Mountpoint {
  id: number;
  name: string;
  url: string;
  mount: string;
  station_id?: number;
}

interface BuskRadio {
  id: number;
  name: string;
  description?: string;
}

interface MixVibes {
  id: number;
  name: string;
  genres?: Array<string | { label: string; name?: string; id?: string }>;
  tags?: Array<string | { label: string; name?: string; id?: string }>;
  mood?: string | { label: string; name?: string; id?: string };
  year_from?: number | null;
  year_to?: number | null;
}

type RepeatMode = 'off' | 'all' | 'one';

interface RouteHandlerProps {
  setActiveTab: (tab: string | null) => void;
  activeTab: string | null;
}

interface PendingSettings {
  width: number | null;
  height: number | null;
  isOpen: boolean | null;
}

// Component to handle route changes and clear activeTab
const RouteHandler: FC<RouteHandlerProps> = ({ setActiveTab, activeTab }) => {
  const location = useLocation();

  useEffect(() => {
    const currentPath = location.pathname;

    // Don't clear activeTab if we're navigating to related pages
    const relatedPages = [
      '/AlbumPage/',  // Album pages (from Catalog)
      '/ArtistPage/', // Legacy artist URLs (redirect to /artist/)
      '/artist/',     // Canonical artist pages (slug, public_id, or numeric id)
      '/genre/',      // Genre pages (from Catalog/Home)
      '/tag/',        // Tag pages (from Catalog/Home)
    ];

    const isRelatedPage = relatedPages.some(route => currentPath.startsWith(route));

    // Only clear activeTab if we're not on a main route and not on a related page
    if (!['/', '/Catalog', '/Search'].includes(currentPath) && !isRelatedPage) {
      setActiveTab(null);
    }
  }, [location.pathname, setActiveTab, activeTab]);

  return null;
};

const AppContent: FC = () => {
  const { registerLogoutCallback, user, loading, token, loginWithToken } = useAuth();
  const location = useLocation();
  const pathLower = location.pathname.toLowerCase();
  const isLandingPageRoute = pathLower === '/landingpage';
  const isRegisterRoute = pathLower === '/register';
  const isLoginRoute = pathLower === '/login';
  const isBroadcasterRoute = pathLower === '/broadcaster';
  const isForgotPasswordRoute = pathLower === '/forgot-password';
  const isResetPasswordRoute = pathLower === '/reset-password';
  const isEmbedRoute = pathLower.startsWith('/embed/');
  const isHomeRoute = location.pathname === '/';
  const showLandingOnly = isLandingPageRoute || (isHomeRoute && !user);
  const showRegisterOnly = isRegisterRoute;
  const showLoginOnly = isLoginRoute;
  const showForgotPasswordOnly = isForgotPasswordRoute;
  const showResetPasswordOnly = isResetPasswordRoute;
  const [gatedCode, setGatedCode] = useState<string | null>(null);
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [selectedMountpoint, setSelectedMountpoint] = useState<Mountpoint | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<Track | Station | Mountpoint | null>(null);
  const [currentTrackIndex, setCurrentTrackIndex] = useState<number | null>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [handleNextTrack, setHandleNextTrack] = useState<() => void>(() => () => { });
  const [handlePrevTrack, setHandlePrevTrack] = useState<() => void>(() => () => { });
  const [isShuffleMode, setIsShuffleMode] = useState<boolean>(false);
  const [repeatMode, setRepeatMode] = useState<RepeatMode>('off');
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [selectedBuskRadio, setSelectedBuskRadio] = useState<BuskRadio | null>(null);
  const [selectedMixVibes, setSelectedMixVibes] = useState<MixVibes | null>(null);
  const [streamListeningId, setStreamListeningId] = useState<number | null>(null);
  const [infoSidebarWidth, setInfoSidebarWidth] = useState<number>(400); // Default width (25vw of 1600px screen)
  const [infoSidebarWidthPercent, setInfoSidebarWidthPercent] = useState<number>(25); // Store as percentage for API
  const [isInfoSidebarCollapsed, setIsInfoSidebarCollapsed] = useState<boolean>(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);
  const [isMobileInfoSidebarOpen, setIsMobileInfoSidebarOpen] = useState<boolean>(false);
  const [isMobileNavbarHidden, setIsMobileNavbarHidden] = useState<boolean>(false);
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() => (
    typeof window !== 'undefined' ? window.innerWidth <= 767 : false
  ));
  const [infoSidebarHeight, setInfoSidebarHeight] = useState<number>(250); // Store infoSidebar height
  const [infoSidebarSettingsLoaded, setInfoSidebarSettingsLoaded] = useState<boolean>(false); // Track if settings have been loaded
  const saveSettingsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSettingsRef = useRef<PendingSettings>({ width: null, height: null, isOpen: null }); // Track pending changes
  const settingsLoadedRef = useRef<boolean>(false); // Track if settings have been loaded
  const contentRef = useRef<HTMLDivElement | null>(null);
  const mobileNavbarHiddenRef = useRef<boolean>(false);
  const scrollPositionsRef = useRef<Map<EventTarget | Document, number>>(new Map());
  const [hasInfoSidebarBeenDismissed, setHasInfoSidebarBeenDismissed] = useState<boolean>(() => {
    // Check localStorage on mount
    return localStorage.getItem('infoSidebarDismissed') === 'true';
  });
  const prevSelectedMixVibes = useRef(null);
  const isFetchingMixVibeTrack = useRef(false);
  // Client-side queue of upcoming MixVibe tracks, kept topped up to
  // MIXVIBE_QUEUE_SIZE. Skipping pops the next one instantly (no network
  // round trip, no flicker) and a refill for MIXVIBE_REFILL_COUNT more fires
  // in the background once the queue drops to MIXVIBE_REFILL_THRESHOLD.
  const mixVibeQueueRef = useRef<Track[]>([]);
  const isRefillingMixVibeQueueRef = useRef(false);
  // Bumped on every skip so an out-of-order fetch response (fast repeated
  // skips racing each other over the network) can be told apart from the
  // latest one - without this, an older response resolving after a newer
  // one would silently snap playback back to an earlier track.
  const mixVibeRequestSeqRef = useRef(0);
  const prevHasSelection = useRef(false); // Track previous selection state

  // Artist pages are unlocked when user has connected accounts - no layout mode change
  const isArtistMode = false;

  useEffect(() => {
    mobileNavbarHiddenRef.current = isMobileNavbarHidden;
  }, [isMobileNavbarHidden]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const mobileNavbarHeight = 'calc(58px + env(safe-area-inset-top, 0px))';
    document.documentElement.style.setProperty('--mobile-navbar-height', mobileNavbarHeight);
    document.documentElement.style.setProperty('--mobile-sticky-tabs-top', mobileNavbarHeight);
    document.documentElement.style.setProperty('--app-navbar-offset', mobileNavbarHeight);

    return () => {
      document.documentElement.style.removeProperty('--mobile-navbar-height');
      document.documentElement.style.removeProperty('--mobile-sticky-tabs-top');
      document.documentElement.style.removeProperty('--app-navbar-offset');
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const isMobileViewport = () => window.innerWidth <= 767;
    let touchStartY: number | null = null;

    const resolveScrollContainer = (target?: EventTarget | null): HTMLElement | Document => {
      if (target instanceof HTMLElement) {
        let node: HTMLElement | null = target;

        while (node) {
          const styles = window.getComputedStyle(node);
          const overflowY = styles.overflowY;
          const canScroll =
            (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
            node.scrollHeight > node.clientHeight + 8;

          if (canScroll) {
            return node;
          }

          node = node.parentElement;
        }
      }

      return contentRef.current || document;
    };

    const getScrollTop = (container: HTMLElement | Document) => {
      if (container instanceof HTMLElement) {
        return Math.max(container.scrollTop, 0);
      }

      const doc = document.documentElement;
      return Math.max(window.scrollY || doc.scrollTop || document.body.scrollTop || 0, 0);
    };

    const setNavbarHidden = (hidden: boolean) => {
      if (mobileNavbarHiddenRef.current === hidden) {
        return;
      }

      mobileNavbarHiddenRef.current = hidden;
      setIsMobileNavbarHidden(hidden);
    };

    const updateNavbarVisibility = (currentScrollTop: number) => {
      if (!isMobileViewport()) {
        setNavbarHidden(false);
        return;
      }

      const previousScrollTop = scrollPositionsRef.current.get(document) ?? 0;
      const delta = currentScrollTop - previousScrollTop;

      if (currentScrollTop <= 8) {
        setNavbarHidden(false);
      } else if (delta >= 4 && currentScrollTop > 64) {
        setNavbarHidden(true);
      } else if (delta <= -4) {
        setNavbarHidden(false);
      }
    };

    const handleDocumentScroll = (event: Event) => {
      const container = resolveScrollContainer(event.target);
      const currentScrollTop = getScrollTop(container);
      const previousScrollTop = scrollPositionsRef.current.get(container) ?? currentScrollTop;
      const delta = currentScrollTop - previousScrollTop;

      scrollPositionsRef.current.set(container, currentScrollTop);
      scrollPositionsRef.current.set(document, currentScrollTop);

      if (!isMobileViewport()) {
        setNavbarHidden(false);
        return;
      }

      if (currentScrollTop <= 8) {
        setNavbarHidden(false);
      } else if (delta >= 4 && currentScrollTop > 64) {
        setNavbarHidden(true);
      } else if (delta <= -4) {
        setNavbarHidden(false);
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (!isMobileViewport() || event.touches.length === 0) {
        return;
      }

      touchStartY = event.touches[0].clientY;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!isMobileViewport() || touchStartY === null || event.touches.length === 0) {
        return;
      }

      const currentY = event.touches[0].clientY;
      const deltaY = touchStartY - currentY;
      const currentScrollTop = getScrollTop(resolveScrollContainer(event.target));

      if (currentScrollTop <= 8) {
        setNavbarHidden(false);
      } else if (deltaY >= 12 && currentScrollTop > 64) {
        setNavbarHidden(true);
        touchStartY = currentY;
      } else if (deltaY <= -10) {
        setNavbarHidden(false);
        touchStartY = currentY;
      }
    };

    const handleTouchEnd = () => {
      touchStartY = null;
    };

    const handleResize = () => {
      if (!isMobileViewport()) {
        setNavbarHidden(false);
      }
    };

    document.addEventListener('scroll', handleDocumentScroll, {
      passive: true,
      capture: true
    });
    document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
      capture: true
    });
    document.addEventListener('touchmove', handleTouchMove, {
      passive: true,
      capture: true
    });
    document.addEventListener('touchend', handleTouchEnd, {
      passive: true,
      capture: true
    });
    window.addEventListener('resize', handleResize);
    const initialScrollTop = getScrollTop(contentRef.current || document);
    scrollPositionsRef.current.set(contentRef.current || document, initialScrollTop);
    scrollPositionsRef.current.set(document, initialScrollTop);
    updateNavbarVisibility(initialScrollTop);

    return () => {
      document.removeEventListener('scroll', handleDocumentScroll, true);
      document.removeEventListener('touchstart', handleTouchStart, true);
      document.removeEventListener('touchmove', handleTouchMove, true);
      document.removeEventListener('touchend', handleTouchEnd, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateViewportFlags = () => {
      const nextIsMobile = window.innerWidth <= 767;
      setIsMobileViewport(nextIsMobile);

      if (nextIsMobile) {
        setIsMobileInfoSidebarOpen(false);
      }
    };

    updateViewportFlags();
    window.addEventListener('resize', updateViewportFlags);

    return () => window.removeEventListener('resize', updateViewportFlags);
  }, []);

  useEffect(() => {
    const contentNode = contentRef.current;

    if (!contentNode) {
      return;
    }

    const stickyTabSelectors = [
      '[role="tablist"]',
      '[class*="_tabBar__"]',
      '[class*="_tabNavigation__"]',
      '[class*="_tabsContainer__"]',
      '[class*="_tabsRow__"]',
      '[class*="_tabsLeft__"]',
      '[class*="_tabContainer__"]',
      '[class*="_tabs__"]',
      '.tabContainer'
    ].join(', ');

    const markStickyTabs = () => {
      contentNode.querySelectorAll('[data-mobile-sticky-tabs="true"]').forEach((node) => {
        node.removeAttribute('data-mobile-sticky-tabs');
      });

      contentNode.querySelectorAll(stickyTabSelectors).forEach((node) => {
        if (!(node instanceof HTMLElement)) {
          return;
        }

        if (node.dataset.disableMobileStickyTabs === 'true') {
          return;
        }

        const className = node.className || '';
        if (typeof className === 'string' && className.includes('content-tabs')) {
          return;
        }

        const tabCount = node.querySelectorAll('button, [role="tab"]').length;
        if (tabCount < 2 || node.getClientRects().length === 0) {
          return;
        }

        node.setAttribute('data-mobile-sticky-tabs', 'true');
      });
    };

    const observer = new MutationObserver(() => {
      markStickyTabs();
    });

    markStickyTabs();
    observer.observe(contentNode, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, [location.pathname, location.search]);

  useEffect(() => {
    mobileNavbarHiddenRef.current = false;
    setIsMobileNavbarHidden(false);
    scrollPositionsRef.current = new Map();
  }, [location.pathname, location.search]);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const modalSelector = [
      '[aria-modal="true"]',
      '[role="dialog"]'
    ].join(',');

    type DisabledState = {
      ariaHidden: string | null;
      inert: boolean;
    };

    const disabledElements = new Map<HTMLElement, DisabledState>();

    const isVisible = (element: Element) => {
      if (!(element instanceof HTMLElement)) return false;
      return element.getClientRects().length > 0;
    };

    const restoreDisabledElements = () => {
      disabledElements.forEach((state, element) => {
        if (state.ariaHidden === null) {
          element.removeAttribute('aria-hidden');
        } else {
          element.setAttribute('aria-hidden', state.ariaHidden);
        }
        (element as any).inert = state.inert;
        element.removeAttribute('data-app-modal-disabled');
      });
      disabledElements.clear();
      document.body.classList.remove('app-modal-open');
    };

    const disableElement = (element: HTMLElement) => {
      if (disabledElements.has(element)) return;
      disabledElements.set(element, {
        ariaHidden: element.getAttribute('aria-hidden'),
        inert: Boolean((element as any).inert),
      });
      element.setAttribute('aria-hidden', 'true');
      (element as any).inert = true;
      element.setAttribute('data-app-modal-disabled', 'true');
    };

    const updateModalIsolation = () => {
      restoreDisabledElements();

      const visibleModals = Array.from(document.querySelectorAll(modalSelector)).filter(isVisible);
      const modal = visibleModals[visibleModals.length - 1];

      if (!(modal instanceof HTMLElement)) {
        return;
      }

      document.body.classList.add('app-modal-open');

      let childOnModalPath: HTMLElement = modal;
      let parent = modal.parentElement;

      while (parent && parent !== document.body) {
        Array.from(parent.children).forEach((sibling) => {
          if (sibling !== childOnModalPath && sibling instanceof HTMLElement) {
            disableElement(sibling);
          }
        });

        childOnModalPath = parent;
        parent = parent.parentElement;
      }

      Array.from(document.body.children).forEach((sibling) => {
        if (sibling !== childOnModalPath && sibling instanceof HTMLElement) {
          disableElement(sibling);
        }
      });
    };

    const observer = new MutationObserver(updateModalIsolation);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-modal', 'role'],
      childList: true,
      subtree: true,
    });

    updateModalIsolation();

    return () => {
      observer.disconnect();
      restoreDisabledElements();
    };
  }, []);

  // Warm the Location route and map topology during idle time.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const connection = (navigator as any)?.connection;
    const isConstrainedNetwork =
      connection?.saveData === true ||
      connection?.effectiveType === 'slow-2g' ||
      connection?.effectiveType === '2g';

    if (isConstrainedNetwork) return undefined;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let idleId: number | null = null;
    let cancelled = false;

    const warmLocationExperience = () => {
      if (cancelled) return;

      // Route/module preloading
      import('./components/Location').catch(() => { });
      import('./components/UnifiedLocationMap').catch(() => { });
      import('./components/Map').catch(() => { });

      // Topology data prefetch for react-simple-maps layers
      const topologyUrls = [
        'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json',
        'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json',
        'https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json'
      ];
      topologyUrls.forEach((url) => {
        fetch(url).catch(() => { });
      });
    };

    if ('requestIdleCallback' in window) {
      idleId = (window as any).requestIdleCallback?.(warmLocationExperience, { timeout: 2500 }) ?? null;
    } else {
      timeoutId = setTimeout(warmLocationExperience, 1200);
    }

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (idleId && 'cancelIdleCallback' in window) {
        (window as any).cancelIdleCallback?.(idleId);
      }
    };
  }, []);

  // Register logout callback to stop audio and clear states
  useEffect(() => {
    const stopAudioAndClearStates = () => {
      // Stop audio and clear states on logout

      // Stop any playing audio by clearing the source
      const audioElement = document.querySelector('audio');
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
        audioElement.load();
      }

      // Clear all audio-related states
      setSelectedStation(null);
      setSelectedTrack(null);
      setSelectedMountpoint(null);
      setSelectedBuskRadio(null);
      setSelectedMixVibes(null);
      setStreamListeningId(null);
      setIsPlaying(false);
      setSelectedItem(null);
      setCurrentTrackIndex(null);
      setTracks([]);
    };

    const cleanup = registerLogoutCallback(stopAudioAndClearStates);
    return cleanup;
  }, []); // Remove registerLogoutCallback from dependencies to prevent infinite loop

  // Clear cached artist/album/track query data on logout so it can't leak into the next session
  useEffect(() => {
    const cleanup = registerLogoutCallback(() => queryClient.clear());
    return cleanup;
  }, []); // Remove registerLogoutCallback from dependencies to prevent infinite loop

  // Any recorded play/station/ether event makes cached ['listening-history', ...] stale.
  // Mounted readers (Recent Listening, Listen Again, ...) refetch once per burst instead of
  // each component re-fetching on its own listener.
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const handleHistoryUpdated = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['listening-history'] });
      }, 1000);
    };
    window.addEventListener('listening-history-updated', handleHistoryUpdated);
    return () => {
      window.removeEventListener('listening-history-updated', handleHistoryUpdated);
      if (timeout) clearTimeout(timeout);
    };
  }, []);

  // Desktop (Electron) sign-in: the token arrives here after the system-browser
  // login handoff completes (busk://auth-callback) — see desktop/preload.js.
  // Hydrate in place via loginWithToken instead of reloading the whole app
  // bundle, so returning from the browser feels instant rather than rebooting.
  useEffect(() => {
    const electronAPI = (window as any).electronAPI;
    if (!electronAPI?.isElectron) return;
    electronAPI.onAuthToken((token: string) => {
      loginWithToken(token).catch((error) => {
        console.error('Failed to complete desktop sign-in:', error);
      });
    });
  }, []); // Empty deps: preload's onAuthToken has no unsubscribe, so re-running this would stack duplicate listeners

  const handleAlbumNextTrack = useCallback((nextTrackHandler: () => void) => {
    setHandleNextTrack(() => nextTrackHandler);
  }, []);

  const handleAlbumPrevTrack = useCallback((prevTrackHandler: () => void) => {
    setHandlePrevTrack(() => prevTrackHandler);
  }, []);

  const handleLibraryNextTrack = useCallback((nextTrackHandler: () => void) => {
    setHandleNextTrack(() => nextTrackHandler);
  }, []);

  const handleLibraryPrevTrack = (handlePrevTrack: () => void): void => {
    handlePrevTrack();
  };



  const changeTrack = (track: Track): void => {
    setSelectedTrack(track);
  };

  const handleTrackSelect = (track: Track): void => {
    setSelectedItem(track);
    setSelectedStation(null);
    setSelectedMountpoint(null);
    setSelectedBuskRadio(null);
    setSelectedMixVibes(null);
    setSelectedTrack(track);
    setCurrentTrackIndex(0);
  };


  const handleMountpointSelect = (mountpoint: Mountpoint | string | { url: string }): void => {
    startTransition(() => {
      const normalized: Mountpoint | { url: string } = typeof mountpoint === 'string'
        ? { url: mountpoint }
        : mountpoint;
      setSelectedItem(normalized as Track | Station | Mountpoint | null);
      setSelectedMountpoint(normalized as Mountpoint);
      setSelectedTrack(null);
      setSelectedStation(null);
      setSelectedBuskRadio(null);
      setSelectedMixVibes(null);
    });
  };

  const handleStationSelect = (station: Station): void => {
    setSelectedItem(station);
  };

  const handleSpacebarKeyPress = (): void => {
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    const queueRef = { tracks: [], index: 0 };

    const registerQueueHandlers = () => {
      setHandleNextTrack(() => () => {
        if (!queueRef.tracks.length) return;
        const nextIndex = queueRef.index + 1;
        if (nextIndex >= queueRef.tracks.length) return;
        queueRef.index = nextIndex;
        const nextTrack = queueRef.tracks[nextIndex];
        setSelectedTrack(nextTrack);
        setCurrentTrackIndex(nextIndex);
      });

      setHandlePrevTrack(() => () => {
        if (!queueRef.tracks.length) return;
        const prevIndex = queueRef.index - 1;
        if (prevIndex < 0) return;
        queueRef.index = prevIndex;
        const prevTrack = queueRef.tracks[prevIndex];
        setSelectedTrack(prevTrack);
        setCurrentTrackIndex(prevIndex);
      });
    };

    const handleChartsPlayTrack = (event) => {
      const track = event?.detail?.track;
      const index = Number.isInteger(event?.detail?.index) ? event.detail.index : 0;
      const queue = Array.isArray(event?.detail?.queue) ? event.detail.queue : [];
      if (!track?.location) {
        return;
      }

      queueRef.tracks = queue.length > 0 ? queue : [track];
      queueRef.index = Math.max(0, Math.min(index, queueRef.tracks.length - 1));
      registerQueueHandlers();

      setSelectedStation(null);
      setSelectedMountpoint(null);
      setSelectedBuskRadio(null);
      setSelectedMixVibes(null);
      setSelectedTrack(track);
      setCurrentTrackIndex(queueRef.index);
    };

    window.addEventListener('busk:play-track', handleChartsPlayTrack);
    return () => window.removeEventListener('busk:play-track', handleChartsPlayTrack);
  }, []);

  const handleInfoSidebarWidthChange = (width: number): void => {
    setInfoSidebarWidth(width);
    // Convert pixel width to viewport width percentage
    const widthPercent = (width / window.innerWidth) * 100;
    setInfoSidebarWidthPercent(widthPercent);
    // Mark width as pending
    pendingSettingsRef.current.width = widthPercent;
    scheduleSettingsSave();
  };

  const handleInfoSidebarHeightChange = (height: number): void => {
    setInfoSidebarHeight(height);
    // Mark height as pending
    pendingSettingsRef.current.height = height;
    scheduleSettingsSave();
  };

  const scheduleSettingsSave = (): void => {
    // Clear any existing timeout
    if (saveSettingsTimeoutRef.current) {
      clearTimeout(saveSettingsTimeoutRef.current);
    }

    // Schedule save after 5 seconds of no changes
    saveSettingsTimeoutRef.current = setTimeout(() => {
      savePendingSettings();
    }, 5000); // Wait 5 seconds for user to "declare" the change stable
  };

  const savePendingSettings = async (): Promise<void> => {
    if (!user?.id) return;

    const pending = pendingSettingsRef.current;
    const settingsToSave: { [key: string]: number | boolean } = {};

    // Only include settings that have pending changes
    if (pending.width !== null) {
      settingsToSave.infoSidebarWidth = pending.width;
    }
    if (pending.height !== null) {
      settingsToSave.infoSidebarHeight = pending.height;
    }
    if (pending.isOpen !== null) {
      settingsToSave.infoSidebarIsOpen = pending.isOpen;
    }

    // If there's nothing to save, return
    if (Object.keys(settingsToSave).length === 0) {
      return;
    }

    try {
      if (!token) return;
      // Save to user_settings table (infosidebar_width, infosidebar_height, infosidebar_is_open)
      await axios.put(`${API_ENDPOINT}/user-settings/bulk`, {
        settings: settingsToSave
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      queryClient.setQueryData(userSettingsQuery().queryKey, (prev) => (prev ? { ...prev, ...settingsToSave } : prev));

      pendingSettingsRef.current = { width: null, height: null, isOpen: null };
    } catch (error: any) {
      // 404 = user-settings route not available; clear pending so we don't keep retrying
      if (error?.response?.status === 404) {
        pendingSettingsRef.current = { width: null, height: null, isOpen: null };
      } else {
        console.error('Error saving InfoSidebar settings:', error);
      }
    }
  };

  const handleInfoSidebarCollapseChange = (collapsed: boolean): void => {
    setIsInfoSidebarCollapsed(collapsed);
    // Mark isOpen as pending (isOpen = !collapsed)
    pendingSettingsRef.current.isOpen = !collapsed;
    scheduleSettingsSave();

    // If user manually collapses the sidebar, mark it as dismissed
    if (collapsed && !hasInfoSidebarBeenDismissed) {
      setHasInfoSidebarBeenDismissed(true);
      localStorage.setItem('infoSidebarDismissed', 'true');
    }
  };

  // Load InfoSidebar preferences from user_settings table (same as Recent tab)
  useEffect(() => {
    const loadInfoSidebarSettings = async () => {
      if (!user?.id || !token) {
        if (!selectedTrack && !selectedStation && !selectedMountpoint && !selectedBuskRadio && !selectedMixVibes) {
          setIsInfoSidebarCollapsed(true);
        }
        setInfoSidebarSettingsLoaded(true);
        settingsLoadedRef.current = true;
        return;
      }

      try {
        // Shared with History/MixVibes on Home — one request instead of three.
        const settings = await queryClient.fetchQuery(userSettingsQuery());

        // Apply all settings BEFORE marking as loaded to prevent visual snap
        if (settings.infoSidebarWidth !== undefined) {
          const widthPercent = settings.infoSidebarWidth;
          setInfoSidebarWidthPercent(widthPercent);
          // Convert percentage to pixels
          const widthPixels = (widthPercent / 100) * window.innerWidth;
          setInfoSidebarWidth(widthPixels);
        }
        if (settings.infoSidebarHeight !== undefined) {
          setInfoSidebarHeight(settings.infoSidebarHeight);
        }
        if (settings.infoSidebarIsOpen !== undefined) {
          // Apply the saved isOpen state (isOpen = !collapsed)
          const shouldBeCollapsed = !settings.infoSidebarIsOpen;
          setIsInfoSidebarCollapsed(shouldBeCollapsed);
        } else {
          // If no saved preference, collapse if nothing is selected
          if (!selectedTrack && !selectedStation && !selectedMountpoint && !selectedBuskRadio && !selectedMixVibes) {
            setIsInfoSidebarCollapsed(true);
          }
        }

        // Mark as loaded AFTER all settings are applied
        settingsLoadedRef.current = true;
        setInfoSidebarSettingsLoaded(true);
      } catch (error: any) {
        // 404 = user-settings route not available (e.g. older backend); use defaults without logging error
        if (error?.response?.status === 404) {
          if (!selectedTrack && !selectedStation && !selectedMountpoint && !selectedBuskRadio && !selectedMixVibes) {
            setIsInfoSidebarCollapsed(true);
          }
        } else {
          console.error('Error loading InfoSidebar settings:', error);
          if (!selectedTrack && !selectedStation && !selectedMountpoint && !selectedBuskRadio && !selectedMixVibes) {
            setIsInfoSidebarCollapsed(true);
          }
        }
        settingsLoadedRef.current = true;
        setInfoSidebarSettingsLoaded(true);
      }
    };

    loadInfoSidebarSettings();
  }, [user?.id, token]);

  // Expand info sidebar when something is selected - but only on first click if not dismissed
  // Only apply this logic after settings have been loaded, so we don't override saved preferences
  useEffect(() => {
    // Don't apply auto-expand logic until settings are loaded
    if (!settingsLoadedRef.current) return;

    const hasSelection: boolean = !!(selectedTrack || selectedStation || selectedMountpoint || selectedBuskRadio || selectedMixVibes);

    // Check if this is a transition from no selection to selection (first click)
    const isFirstSelection = hasSelection && !prevHasSelection.current;

    if (isFirstSelection && !hasInfoSidebarBeenDismissed && !isMobileViewport) {
      // This is the first time something is selected and sidebar hasn't been dismissed
      setIsInfoSidebarCollapsed(false);
    }

    // Update the ref for next comparison
    prevHasSelection.current = hasSelection;
  }, [selectedTrack, selectedStation, selectedMountpoint, selectedBuskRadio, selectedMixVibes, hasInfoSidebarBeenDismissed, isMobileViewport]);

  // Handler for MixVibes customization updates
  const handleMixVibesUpdate = (updatedMixVibes: MixVibes): void => {
    setSelectedMixVibes(updatedMixVibes);
    // This will trigger the useEffect below to fetch a new track
  };

  const MIXVIBE_QUEUE_SIZE = 10;
  const MIXVIBE_REFILL_THRESHOLD = 5;

  const formatMixVibeTrack = (trackData: any): Track | null => {
    if (!trackData || !trackData.id) return null;
    return {
      id: trackData.id,
      title: trackData.title,
      artist: trackData.artistname || trackData.artist || 'Unknown Artist',
      location: trackData.location,
      artist_id: trackData.artistid || trackData.artist_id,
      album_id: trackData.albumid || trackData.album_id,
      album_image: trackData.album_image,
      albumname: trackData.albumname,
      artist_image: trackData.artist_image,
      artist_name: trackData.artistname || trackData.artist,
    };
  };

  // Fetch up to `count` random tracks matching MixVibe criteria in one
  // request, excluding any track ids already held (queued or playing) - the
  // backend scores/filters the whole matching set server-side and hands back
  // random picks from it, instead of shipping the whole candidate pool (a
  // popular genre's full pool was ballooning to 1MB+ responses) for the
  // client to sample from itself.
  const fetchMixVibeTracksBatch = async (
    mixVibe: MixVibes,
    count: number,
    excludeTrackIds: number[] = []
  ): Promise<Track[]> => {
    try {
      const params: Record<string, string | number> = {};

      if (mixVibe.genres && mixVibe.genres.length > 0) {
        const genreValues = mixVibe.genres
          .map(genre => {
            if (typeof genre === 'string') return genre;
            return genre.label || genre.name || genre.id || genre;
          })
          .filter(g => g);
        if (genreValues.length > 0) {
          params.genres = genreValues.join(',');
        }
      }

      if (mixVibe.tags && mixVibe.tags.length > 0) {
        const tagValues = mixVibe.tags
          .map(tag => {
            if (typeof tag === 'string') return tag;
            return tag.label || tag.name || tag.id || tag;
          })
          .filter(t => t);
        if (tagValues.length > 0) {
          params.tags = tagValues.join(',');
        }
      }

      if (mixVibe.mood && (!params.tags || (typeof params.tags === 'string' && params.tags.length === 0))) {
        const moodValue = typeof mixVibe.mood === 'string'
          ? mixVibe.mood
          : (mixVibe.mood.label || mixVibe.mood.name || mixVibe.mood.id);
        if (moodValue) {
          params.mood = moodValue;
        }
      }

      if (mixVibe.id != null) {
        params.mixvibeId = mixVibe.id;
      }
      if (mixVibe.year_from != null) {
        params.year_from = mixVibe.year_from;
      }
      if (mixVibe.year_to != null) {
        params.year_to = mixVibe.year_to;
      }

      params.mode = 'single';
      params.count = count;
      if (excludeTrackIds.length > 0) {
        params.excludeTrackIds = excludeTrackIds.join(',');
      }

      const response = await axios.get(`${API_ENDPOINT}/mixvibes`, { params });
      const data = response.data;
      const rawTracks = Array.isArray(data) ? data : (data ? [data] : []);
      const tracks = rawTracks.map(formatMixVibeTrack).filter((t): t is Track => t != null);
      return tracks;
    } catch (error: any) {
      console.error('Error fetching MixVibe tracks:', error);
      if (error.response?.status === 404) {
        console.warn('No tracks found matching MixVibe criteria:', error.response.data);
      }
      return [];
    }
  };

  // Top the queue back up to MIXVIBE_QUEUE_SIZE in the background, excluding
  // whatever's already queued or currently playing so a refill can't hand
  // back a duplicate.
  const refillMixVibeQueue = useCallback((mixVibe: MixVibes, playingTrackId?: number | null): void => {
    if (isRefillingMixVibeQueueRef.current) return;
    const needed = MIXVIBE_QUEUE_SIZE - mixVibeQueueRef.current.length;
    if (needed <= 0) return;
    isRefillingMixVibeQueueRef.current = true;
    const excludeIds = mixVibeQueueRef.current
      .map(t => t.id)
      .concat(playingTrackId != null ? [playingTrackId] : [])
      .filter((id): id is number => id != null);
    fetchMixVibeTracksBatch(mixVibe, needed, excludeIds).then((tracks) => {
      mixVibeQueueRef.current = mixVibeQueueRef.current.concat(tracks);
    }).finally(() => {
      isRefillingMixVibeQueueRef.current = false;
    });
  }, []);

  // Handler for MixVibe next track - pops instantly from the pre-filled
  // queue (no network wait, no flicker) and tops the queue back up in the
  // background once it runs low.
  const handleMixVibeNextTrack = useCallback((): void => {
    if (!selectedMixVibes) return;

    const currentTrackId = selectedTrack?.id;

    if (mixVibeQueueRef.current.length > 0) {
      const nextTrack = mixVibeQueueRef.current.shift()!;
      setSelectedTrack(nextTrack);
      if (mixVibeQueueRef.current.length <= MIXVIBE_REFILL_THRESHOLD) {
        refillMixVibeQueue(selectedMixVibes, nextTrack.id);
      }
      return;
    }

    // Queue's empty (shouldn't normally happen) - fall back to a direct
    // fetch, showing nothing until it arrives rather than looping the
    // outgoing track.
    const seq = ++mixVibeRequestSeqRef.current;
    setSelectedTrack(null);
    fetchMixVibeTracksBatch(selectedMixVibes, 1, currentTrackId != null ? [currentTrackId] : []).then((tracks) => {
      if (mixVibeRequestSeqRef.current !== seq) return;
      if (tracks[0]) {
        setSelectedTrack(tracks[0]);
        refillMixVibeQueue(selectedMixVibes, tracks[0].id);
      } else {
        console.warn('Could not fetch next MixVibe track');
      }
    });
  }, [selectedMixVibes, selectedTrack, refillMixVibeQueue]);

  // Set MixVibe next track handler when MixVibe is selected
  // Only manage handler if MixVibes is actually selected - don't clear it otherwise
  // Other components (AlbumPage, Library) will set their own handlers
  useEffect(() => {
    if (selectedMixVibes) {
      setHandleNextTrack(() => handleMixVibeNextTrack);
    }
    // Don't clear handler when MixVibe is deselected - let other components manage their handlers
    // Clearing here was overwriting AlbumPage's handler registration
  }, [selectedMixVibes, handleMixVibeNextTrack]);

  // Fetch a fresh MixVibe queue when a MixVibe is selected or updated
  useEffect(() => {
    const mixVibesChanged = selectedMixVibes !== prevSelectedMixVibes.current;

    if (mixVibesChanged && selectedMixVibes && !isFetchingMixVibeTrack.current) {
      isFetchingMixVibeTrack.current = true;

      // A queued batch (if any) was fetched under the previous MixVibe's
      // criteria - discard it rather than handing back tracks that no
      // longer match what's now selected.
      mixVibeQueueRef.current = [];

      // Clear selectedTrack when MixVibe is selected/updated
      setSelectedTrack(null);

      // Fetch a batch of random tracks matching MixVibe criteria - the first
      // one plays immediately, the rest seed the queue for instant skips.
      const setMixVibeTrack = async (mixVibe: MixVibes) => {
        try {
          const tracks = await fetchMixVibeTracksBatch(mixVibe, MIXVIBE_QUEUE_SIZE);
          if (tracks.length > 0) {
            const [first, ...rest] = tracks;
            mixVibeQueueRef.current = rest;
            setSelectedTrack(first);
          }
        } finally {
          isFetchingMixVibeTrack.current = false;
        }
      };

      setMixVibeTrack(selectedMixVibes);
      prevSelectedMixVibes.current = selectedMixVibes;
    } else if (mixVibesChanged && !selectedMixVibes) {
      prevSelectedMixVibes.current = null;
    }
  }, [selectedMixVibes]);

  // Landing page (sales pitch) - Navbar + landing content, no Sidebar/Playbar
  if (isEmbedRoute) {
    return (
      <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgb(18,18,18)', color: 'rgb(232,232,232)' }}>Loading...</div>}>
        <Routes>
          <Route path="/embed/track/:id" element={<EmbeddedTrackPlayer />} />
          <Route path="/embed/album/:id" element={<EmbeddedAlbumPlayer />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  if (showLandingOnly) {
    if (loading) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgb(22,22,24)', color: 'rgb(201,201,201)' }}>
          Loading...
        </div>
      );
    }
    if (!gatedCode) {
      return <PromoCodeGate onCodeValid={(code) => setGatedCode(code)} />;
    }
    return (
      <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgb(20,20,22)', color: 'rgb(201,201,201)' }}>Loading...</div>}>
        <LandingPage promoCode={gatedCode} />
      </Suspense>
    );
  }

  // Register - renders outside the player, no Navbar/Sidebar/Playbar
  if (showRegisterOnly) {
    return (
      <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgb(22,22,24)', color: 'rgb(201,201,201)' }}>Loading...</div>}>
        <Register />
      </Suspense>
    );
  }

  // Login - renders outside the player, no Navbar/Sidebar/Playbar
  if (showLoginOnly) {
    return (
      <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgb(22,22,24)', color: 'rgb(201,201,201)' }}>Loading...</div>}>
        <Login />
      </Suspense>
    );
  }

  if (showForgotPasswordOnly) {
    return (
      <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgb(22,22,24)', color: 'rgb(201,201,201)' }}>Loading...</div>}>
        <ForgotPassword />
      </Suspense>
    );
  }

  if (showResetPasswordOnly) {
    return (
      <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgb(22,22,24)', color: 'rgb(201,201,201)' }}>Loading...</div>}>
        <ResetPassword />
      </Suspense>
    );
  }

  return (
    <div className={styles.App}>
      <Navbar
          className={styles.navebar}
          isArtistMode={isArtistMode}
          infoSidebarWidth={infoSidebarWidth}
          onMobileSidebarToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
          isMobileHidden={false}
          onSelectTrack={handleTrackSelect}
        />
        {/* Main Layout Container */}
        <div className={styles.mainLayout}>
          {!isArtistMode && (
            <Sidebar
              activeTab={activeTab}
              selectedTrack={selectedTrack}
              selectedStation={selectedStation}
              selectedMountpoint={selectedMountpoint}
              selectedBuskRadio={selectedBuskRadio}
              selectedMixVibes={selectedMixVibes}
              isMobileOpen={isMobileSidebarOpen}
              onMobileClose={() => setIsMobileSidebarOpen(false)}
              onSelectTrack={handleTrackSelect}
            />
          )}

          {/* Main Content Area */}
          <div
            className={`${styles.mainContent} ${isArtistMode ? styles.ArtistMode : styles.SupporterMode} ${isBroadcasterRoute ? styles.broadcasterMode : ''}`}
            style={{
              marginRight: isArtistMode || isMobileViewport || isInfoSidebarCollapsed ? '0px' : `${infoSidebarWidth}px`,
              transition: 'margin-right 0.3s ease'
            }}
          >
            <RouteHandler setActiveTab={setActiveTab} activeTab={activeTab} />
            <Suspense fallback={<div>Loading...</div>}>
              <div ref={contentRef} className={styles.Content}>
                <Routes>
                  <Route path="/LandingPage" element={<LandingPage />} />
                  <Route path="/" element={<Home selectedStation={selectedStation} setSelectedStation={setSelectedStation} selectedMountpoint={selectedMountpoint} handleMountpointSelect={handleMountpointSelect} setSelectedMountpoint={setSelectedMountpoint} selectedTrack={selectedTrack} setSelectedTrack={setSelectedTrack} currentTrackIndex={currentTrackIndex} setCurrentTrackIndex={setCurrentTrackIndex} setActiveTab={setActiveTab} selectedBuskRadio={selectedBuskRadio} setSelectedBuskRadio={setSelectedBuskRadio} selectedMixVibes={selectedMixVibes} setSelectedMixVibes={setSelectedMixVibes} infoSidebarWidth={infoSidebarWidth} />} />
                  <Route path="/mix-vibes" element={<MixVibesShowcase setSelectedMixVibes={setSelectedMixVibes} />} />
                  <Route path="/Charts" element={<Charts />} />
                  <Route path="/buskradio-create/:id" element={<ProtectedRoute><BuskRadioCreate /></ProtectedRoute>} />
                  <Route path="/buskradioedit/:id" element={<ProtectedRoute><BuskRadioEdit /></ProtectedRoute>} />

                  {/* Auth routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  <Route path="/ActiveMountpoints" element={<ActiveMountpoints selectedMountpoint={selectedMountpoint} handleMountpointSelect={handleMountpointSelect} />} />
                  <Route path="/Admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
                  <Route path="/Admin/messages/:id" element={<ProtectedRoute requireAdmin><AdminMessageThread /></ProtectedRoute>} />
                  <Route path="/editProfile/:id" element={<ProtectedRoute requireAdmin><EditProfile /></ProtectedRoute>} />
                  <Route path="/EditProfile/:id" element={<ProtectedRoute requireAdmin><EditProfile /></ProtectedRoute>} />
                  <Route path="/AlbumCreate" element={<ProtectedRoute><AlbumCreate selectedTrack={selectedTrack} setSelectedTrack={setSelectedTrack} /></ProtectedRoute>} />
                  <Route path="/AlbumEdit/:id" element={<ProtectedRoute><AlbumEdit selectedTrack={selectedTrack} setSelectedTrack={setSelectedTrack} handleTrackSelect={handleTrackSelect} changeTrack={changeTrack} /></ProtectedRoute>} />
                  <Route path="/AlbumList" element={<AlbumList />} />
                  {/* Canonical album route using opaque identifier (public_id or legacy id) */}
                  <Route path="/album/:id" element={
                    <AlbumPage
                      selectedTrack={selectedTrack}
                      setSelectedTrack={setSelectedTrack}
                      currentTrackIndex={currentTrackIndex}
                      setCurrentTrackIndex={setCurrentTrackIndex}
                      setTracks={setTracks}
                      handleNextTrack={handleAlbumNextTrack}
                      handlePrevTrack={handleAlbumPrevTrack}
                      isShuffleMode={isShuffleMode}
                      setIsShuffleMode={setIsShuffleMode}
                      repeatMode={repeatMode}
                      setRepeatMode={setRepeatMode}
                      setActiveTab={setActiveTab}
                    />
                  }
                  />
                  {/* Legacy AlbumPage route kept for backward compatibility, redirects to /album/:id */}
                  <Route path="/AlbumPage/:id" element={<LegacyAlbumRedirect />} />
                  <Route path="/genre/:value" element={<GenreTagPage />} />
                  <Route path="/tag/:value" element={<GenreTagPage />} />
                  <Route path="/ArtistEdit/:id" element={<ProtectedRoute><ArtistEdit selectedTrack={selectedTrack} setSelectedTrack={setSelectedTrack} handleTrackSelect={handleTrackSelect} changeTrack={changeTrack} /></ProtectedRoute>} />
                  <Route path="/ArtistHomepage" element={<ArtistHomepage />} />
                  <Route path="/ArtistPage/:id" element={<LegacyArtistPageRedirect />} />
                  {/* :id = slug, UUID public_id, or numeric DB id (API resolves all via /api/artist/:id) */}
                  <Route path="/artist/:id" element={<ArtistPageRoute selectedTrack={selectedTrack} setSelectedTrack={setSelectedTrack} currentTrackIndex={currentTrackIndex} setCurrentTrackIndex={setCurrentTrackIndex} setSelectedStation={setSelectedStation} infoSidebarWidth={infoSidebarWidth} />} />
                  <Route path="/ArtistDashboard/:id" element={<ProtectedRoute><ArtistDashboard /></ProtectedRoute>} />
                  <Route path="/ArtistList" element={<ProtectedRoute requireAdmin><ArtistList /></ProtectedRoute>} />
                  <Route path="/Broadcast" element={<Broadcast selectedStation={selectedStation} setSelectedStation={setSelectedStation} selectedMountpoint={selectedMountpoint} />} />
                  <Route path="/BroadcastDashboard" element={<BroadcastDashboardTabs selectedStation={selectedStation} setSelectedStation={setSelectedStation} selectedMountpoint={selectedMountpoint} handleMountpointSelect={handleMountpointSelect} setSelectedMountpoint={setSelectedMountpoint} setSelectedBuskRadio={setSelectedBuskRadio} setSelectedMixVibes={setSelectedMixVibes} infoSidebarWidth={infoSidebarWidth} />} />
                  <Route path="/BuskRadio" element={<BuskRadio setSelectedBuskRadio={setSelectedBuskRadio} />} />
                  <Route path="/Broadcaster" element={<IcecastBroadcaster onSelectTrack={handleTrackSelect} />} />
                  <Route path="/Chatrooms" element={<Chatrooms />} />
                  <Route path="/CreateArtistProfile" element={<ProtectedRoute><CreateArtistProfile /></ProtectedRoute>} />
                  <Route path="/CreateSupporterProfile" element={<ProtectedRoute><CreateSupporterProfile /></ProtectedRoute>} />

                  <Route path="/UploadTracks" element={<ProtectedRoute><UploadTracks /></ProtectedRoute>} />
                  <Route path="/UploadSheetMusic" element={<ProtectedRoute><UploadSheetMusic /></ProtectedRoute>} />
                  <Route path="/UploadStem" element={<ProtectedRoute><UploadStem /></ProtectedRoute>} />
                  <Route path="/UploadPacks" element={<ProtectedRoute><UploadPacks /></ProtectedRoute>} />
                  <Route path="/Catalog" element={
                    <Catalog
                      selectedTrack={selectedTrack}
                      setSelectedTrack={setSelectedTrack}
                      currentTrackIndex={currentTrackIndex}
                      setCurrentTrackIndex={setCurrentTrackIndex}
                      setTracks={setTracks}
                      handleNextTrack={handleLibraryNextTrack}
                      handlePrevTrack={handleLibraryPrevTrack}
                      setActiveTab={setActiveTab}
                      setSelectedBuskRadio={setSelectedBuskRadio}
                      repeatMode={repeatMode}
                    />}
                  />
                  <Route path="/Library" element={<Navigate to="/Catalog" replace />} />
                  <Route path="/LibraryGrid" element={<LibraryGrid
                    selectedTrack={selectedTrack}
                    setSelectedTrack={setSelectedTrack}
                    currentTrackIndex={currentTrackIndex}
                    setCurrentTrackIndex={setCurrentTrackIndex}
                    setTracks={setTracks}
                    handleNextTrack={handleLibraryNextTrack}
                    handlePrevTrack={handleLibraryPrevTrack}
                    repeatMode={repeatMode}
                  />} />
                  <Route path="/LinerNotes/:id" element={<LinerNotes selectedTrack={selectedTrack} setSelectedTrack={setSelectedTrack} handleTrackSelect={handleTrackSelect} changeTrack={changeTrack} />} />
                  <Route path="/Likes" element={
                    <Likes
                      selectedTrack={selectedTrack}
                      setSelectedTrack={setSelectedTrack}
                      currentTrackIndex={currentTrackIndex}
                      setCurrentTrackIndex={setCurrentTrackIndex}
                      setTracks={setTracks}
                      handleNextTrack={handleAlbumNextTrack}
                      handlePrevTrack={handlePrevTrack}
                      repeatMode={repeatMode}
                    />} />
                  <Route path="/Ether" element={<Ether selectedMountpoint={selectedMountpoint} handleMountpointSelect={handleMountpointSelect} setSelectedMountpoint={setSelectedMountpoint} />} />
                  <Route path="/EtherGenre" element={<EtherGenre />} />
                  <Route path="/EtherMood" element={<EtherMood />} />
                  <Route path="/EtherInstruments" element={<EtherInstruments />} />
                  <Route path="/EtherTags" element={<EtherTags />} />
                  <Route path="/EtherLocation" element={<EtherLocation />} />
                  <Route path="/RadioStations" element={<RadioStations selectedStation={selectedStation} setSelectedStation={setSelectedStation} />} />
                  <Route path="/Concerts" element={<Concerts />} />
                  <Route path="/Location" element={<Location infoSidebarWidth={infoSidebarWidth} isInfoSidebarCollapsed={isInfoSidebarCollapsed} setSelectedStation={setSelectedStation} selectedStation={selectedStation} />} />
                  <Route path="/Calendar" element={<CalendarPage />} />
                  <Route path="/Profile" element={<Profile />} />
                  <Route path="/Notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
                  <Route path="/Messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
                  <Route path="/ListeningHistory" element={<ListeningHistoryDisplay selectedTrack={selectedTrack} setSelectedTrack={setSelectedTrack} currentTrackIndex={currentTrackIndex} setCurrentTrackIndex={setCurrentTrackIndex} />} />
                  <Route path="/Payments" element={<Payments selectedTrack={selectedTrack} setSelectedTrack={setSelectedTrack} currentTrackIndex={currentTrackIndex} setCurrentTrackIndex={setCurrentTrackIndex} setSelectedStation={setSelectedStation} setSelectedMountpoint={setSelectedMountpoint} />} />
                  <Route path="/SupporterList" element={<SupporterList />} />
                  <Route path="/Playlist/:id" element={<Playlist
                    selectedTrack={selectedTrack}
                    setSelectedTrack={setSelectedTrack}
                    currentTrackIndex={currentTrackIndex}
                    setTracks={setTracks}
                    handleNextTrack={handleLibraryNextTrack}
                    handlePrevTrack={handlePrevTrack}
                    setCurrentTrackIndex={setCurrentTrackIndex}
                    repeatMode={repeatMode}
                  />} />
                  <Route path="/playlist/share/:publicId" element={<Playlist
                    selectedTrack={selectedTrack}
                    setSelectedTrack={setSelectedTrack}
                    currentTrackIndex={currentTrackIndex}
                    setTracks={setTracks}
                    handleNextTrack={handleLibraryNextTrack}
                    handlePrevTrack={handlePrevTrack}
                    setCurrentTrackIndex={setCurrentTrackIndex}
                    repeatMode={repeatMode}
                    isPublic={true}
                  />} />
                  <Route path="/PlaylistList" element={<PlaylistList
                    selectedTrack={selectedTrack}
                    setSelectedTrack={setSelectedTrack}
                    currentTrackIndex={currentTrackIndex}
                    setTracks={setTracks}
                    handleNextTrack={handleLibraryNextTrack}
                    handlePrevTrack={handlePrevTrack}
                    setCurrentTrackIndex={setCurrentTrackIndex}
                  />} />
                  <Route path="/PlaylistManage/:id" element={<ProtectedRoute><PlaylistManage /></ProtectedRoute>} />
                  <Route path="/OfflineMusic" element={<ProtectedRoute><OfflineMusic /></ProtectedRoute>} />
                  <Route path="/OfflineMusic/artist/:key" element={<ProtectedRoute><OfflineMusicArtist /></ProtectedRoute>} />
                  <Route path="/OfflineMusic/collection/:key" element={<ProtectedRoute><OfflineMusicCollection /></ProtectedRoute>} />
                  <Route path="/ProfileIcon" element={<ProfileIcon />} />
                  <Route path="/Sandbox" element={<Sandbox />} />
                  <Route path="/Search" element={<Search setActiveTab={setActiveTab} onSelectTrack={handleTrackSelect} />} />
                  <Route path="/SupporterDashboard" element={<ProtectedRoute><SupporterDashboard /></ProtectedRoute>} />
                  <Route path="/TrackEdit/:id" element={<ProtectedRoute><TrackEdit /></ProtectedRoute>} />
                  <Route path="/TrackForm" element={<TrackForm />} />
                  <Route path="/TrackList" element={<TrackList />} />
                  <Route path="/EditProfile/:id" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
                  <Route path="/UploadForm" element={<ProtectedRoute><UploadForm /></ProtectedRoute>} />
                  <Route path="/VideoUploader" element={<ProtectedRoute><VideoUploader /></ProtectedRoute>} />
                  <Route path="/MyMusic" element={<Songs />} />
                  <Route path="/ZphotoList" element={<ZphotoList />} />
                </Routes>
              </div>
            </Suspense>
            <ListeningHistory
              selectedStation={selectedStation}
              selectedMountpoint={selectedMountpoint}
              selectedTrack={selectedTrack}
              streamListeningId={streamListeningId}
              currentTrackIndex={currentTrackIndex}
              handleNextTrack={handleNextTrack}
            />
          </div>
        </div>

        {/* InfoSidebar - True Sidebar - Only render after settings are loaded to prevent visual snap */}
        {!isArtistMode && !isMobileViewport && infoSidebarSettingsLoaded && (
          <InfoSidebar
            selectedTrack={selectedTrack}
            selectedStation={selectedStation}
            selectedMountpoint={selectedMountpoint}
            selectedBuskRadio={selectedBuskRadio}
            selectedMixVibes={selectedMixVibes}
            onMixVibesUpdate={handleMixVibesUpdate}
            onWidthChange={handleInfoSidebarWidthChange}
            onResizingChange={() => {}}
            isCollapsed={isInfoSidebarCollapsed}
            onCollapseChange={handleInfoSidebarCollapseChange}
            infoSidebarHeight={infoSidebarHeight}
            onInfoSidebarHeightChange={handleInfoSidebarHeightChange}
            isMobileOpen={isMobileInfoSidebarOpen}
            onMobileToggle={() => setIsMobileInfoSidebarOpen(!isMobileInfoSidebarOpen)}
            infoSidebarWidth={infoSidebarWidth}
            onMixVibeSkip={handleNextTrack}
          />
        )}

        {/* Playbar - fixed at viewport bottom, rendered at App root to avoid layout containment issues */}
        {!isArtistMode && (
          <div style={{ display: isBroadcasterRoute && !selectedTrack ? 'none' : 'contents' }}>
            <Playbar
              selectedStation={selectedStation}
              selectedMountpoint={selectedMountpoint}
              selectedTrack={selectedTrack}
              selectedMixVibes={selectedMixVibes}
              setSelectedTrack={setSelectedTrack}
              onTrackStreamReady={setStreamListeningId}
              currentTrackIndex={currentTrackIndex}
              handleNextTrack={handleNextTrack}
              handlePreviousTrack={handlePrevTrack}
              isShuffleMode={isShuffleMode}
              setIsShuffleMode={setIsShuffleMode}
              repeatMode={repeatMode}
              setRepeatMode={setRepeatMode}
              infoSidebarWidth={infoSidebarWidth}
              isInfoSidebarCollapsed={isMobileViewport ? true : isInfoSidebarCollapsed}
            />
          </div>
        )}
    </div>
  );
};

const GateWrapper: FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

const App: FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LikesProvider>
          <NotificationProvider>
            <ToastNotifications />
            <GlobalPhotoUploadStatus />
            <GateWrapper />
          </NotificationProvider>
        </LikesProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
